import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { SalesSheetStatus } from '../../../../interfaces/apis/sales-sheet';
import { DictionaryService } from '../../../../services/dictionary.service';

export interface SalesSheetProgressStep {
  status:
    | SalesSheetStatus.PENDING
    | SalesSheetStatus.PROCESSING
    | SalesSheetStatus.DONE;
  labelKey: string;
}

export type SalesSheetProgressKind =
  | 'progress'
  | 'invalid'
  | 'failed'
  | 'unknown';
export type SalesSheetStepState = 'completed' | 'current' | 'upcoming';

export interface SalesSheetProgressState {
  kind: SalesSheetProgressKind;
  currentIndex: number;
  labelKey?: string;
  fallbackLabel?: string;
}

export const SALES_SHEET_PROGRESS_STEPS: readonly SalesSheetProgressStep[] = [
  {
    status: SalesSheetStatus.PENDING,
    labelKey: 'SalesSheetStatusPending',
  },
  {
    status: SalesSheetStatus.PROCESSING,
    labelKey: 'SalesSheetStatusProcessing',
  },
  {
    status: SalesSheetStatus.DONE,
    labelKey: 'SalesSheetStatusDone',
  },
];

@Component({
  selector: 'app-sales-sheet-status-progress',
  templateUrl: './sales-sheet-status-progress.component.html',
  styleUrl: './sales-sheet-status-progress.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SalesSheetStatusProgressComponent {
  readonly status = input.required<unknown>();
  readonly steps = SALES_SHEET_PROGRESS_STEPS;
  readonly dictionary = inject(DictionaryService);
  readonly viewState = computed(() => salesSheetProgressState(this.status()));

  stepState(index: number): SalesSheetStepState {
    const currentIndex = this.viewState().currentIndex;
    if (index < currentIndex) return 'completed';
    if (index === currentIndex) return 'current';
    return 'upcoming';
  }

  stepLabel(step: SalesSheetProgressStep): string {
    return this.dictionary.get(step.labelKey);
  }

  statusLabel(): string {
    const state = this.viewState();
    if (state.labelKey) return this.dictionary.get(state.labelKey);
    return (
      state.fallbackLabel || this.dictionary.get('SalesSheetStatusUnknown')
    );
  }

  stageAriaLabel(index: number): string {
    const stateKey: Record<SalesSheetStepState, string> = {
      completed: 'SalesSheetStageCompleted',
      current: 'SalesSheetStageCurrent',
      upcoming: 'SalesSheetStageUpcoming',
    };
    return `${this.stepLabel(this.steps[index])}, ${this.dictionary.get(
      stateKey[this.stepState(index)],
    )}`;
  }
}

export function salesSheetProgressState(
  status: unknown,
): SalesSheetProgressState {
  const rawStatus = String(status ?? '').trim();
  const normalizedStatus = rawStatus.toLowerCase();

  const currentIndex = SALES_SHEET_PROGRESS_STEPS.findIndex(
    (step) => step.status.toLowerCase() === normalizedStatus,
  );
  if (currentIndex >= 0) {
    return {
      kind: 'progress',
      currentIndex,
      labelKey: SALES_SHEET_PROGRESS_STEPS[currentIndex].labelKey,
    };
  }

  if (normalizedStatus === SalesSheetStatus.INVALID_SHEET_DATA.toLowerCase()) {
    return {
      kind: 'invalid',
      currentIndex: -1,
      labelKey: 'SalesSheetStatusInvalidData',
    };
  }

  if (normalizedStatus === SalesSheetStatus.FAILED.toLowerCase()) {
    return {
      kind: 'failed',
      currentIndex: -1,
      labelKey: 'SalesSheetStatusFailed',
    };
  }

  return {
    kind: 'unknown',
    currentIndex: -1,
    fallbackLabel: rawStatus,
  };
}
